import type {
  CreateGoalRequest,
  CreateGoalResponse,
  GoalPlatformStateResponse,
  SetGoalPlatformRequest,
  SetGoalPlatformResponse,
  SnapshotInvalidation,
} from "@repo/shared/types";
import { delWithToken, getWithToken, patchWithToken, postWithToken } from "../_client";

const PUBLIC_BASE = "/public/flow/goals";

// --- Goals ---

export function getPublicGoals(token: string) {
  return getWithToken<any[]>(PUBLIC_BASE, token);
}

// §3.9 Option D — client goal management.
export function addPublicGoal(data: CreateGoalRequest, token: string) {
  return postWithToken<CreateGoalResponse>(PUBLIC_BASE, token, data satisfies CreateGoalRequest);
}

export function deletePublicGoal(goalId: string, token: string) {
  return delWithToken<{ success: boolean }>(`${PUBLIC_BASE}/${goalId}`, token);
}

export function getPublicGoal(goalId: string, token: string) {
  return getWithToken<any>(`${PUBLIC_BASE}/${goalId}`, token);
}

export function updatePublicGoal(goalId: string, data: Record<string, unknown>, token: string) {
  return patchWithToken<any & { refreshHints?: SnapshotInvalidation }>(
    `${PUBLIC_BASE}/${goalId}`,
    token,
    data,
  );
}

// --- Platforms (QP-195) ---

export function getPublicGoalPlatforms(goalId: string, token: string, goalType?: string) {
  const params = goalType ? `?goalType=${encodeURIComponent(goalType)}` : "";
  return getWithToken<GoalPlatformStateResponse>(
    `${PUBLIC_BASE}/${goalId}/platforms${params}`,
    token,
  );
}

export function setPublicGoalPlatform(
  goalId: string,
  platformId: string,
  token: string,
  lang?: string,
) {
  return postWithToken<SetGoalPlatformResponse>(`${PUBLIC_BASE}/${goalId}/platform`, token, {
    platformId,
    lang,
  } satisfies SetGoalPlatformRequest);
}

// --- Custom Products ---

export function addPublicCustomProduct(
  goalId: string,
  data: { productId: string; amount?: number; units?: number; weight?: number; lang?: string },
  token: string,
) {
  return postWithToken<any & { refreshHints?: SnapshotInvalidation }>(
    `${PUBLIC_BASE}/${goalId}/products`,
    token,
    data,
  );
}

export function updatePublicCustomProduct(
  productId: string,
  data: { amount?: number; units?: number; weight?: number; lang?: string },
  token: string,
) {
  return patchWithToken<any & { refreshHints?: SnapshotInvalidation }>(
    `${PUBLIC_BASE}/products/${productId}`,
    token,
    data,
  );
}

export function deletePublicCustomProduct(productId: string, token: string) {
  return delWithToken<{ success: boolean; goalId: string; refreshHints?: SnapshotInvalidation }>(
    `${PUBLIC_BASE}/products/${productId}`,
    token,
  );
}

// --- Reference Data ---

export function getPublicProductsWithSuitability(goalId: string, token: string, lang?: string) {
  const params = lang ? `?lang=${lang}` : "";
  return getWithToken<any[]>(`${PUBLIC_BASE}/${goalId}/products-suitability${params}`, token);
}

export function getPublicSuitabilityContext(goalId: string, token: string, lang?: string) {
  const params = lang ? `?lang=${lang}` : "";
  return getWithToken<any>(`${PUBLIC_BASE}/${goalId}/suitability-context${params}`, token);
}

export function getPublicRiskModelOptions(goalId: string, token: string, lang?: string) {
  const params = lang ? `?lang=${lang}` : "";
  return getWithToken<any>(`${PUBLIC_BASE}/${goalId}/risk-model-options${params}`, token);
}
